import { Link, useNavigate } from 'react-router-dom';
import { useUser, UserButton, SignInButton } from '@clerk/clerk-react';
import { Mic, CreditCard, Key, Moon, Sun, Monitor } from 'lucide-react';
import { useApi } from '../../utils/api.js';
import { useState, useEffect } from 'react';
import BYOKSettingsModal from '../BYOKSettingsModal.jsx';
import { sharedStyles } from './sharedStyles.js';
import { useTheme } from '../../context/ThemeContext.jsx';

const Navbar = ({
  variant = 'default', // 'default', 'landing', 'form', 'report'
  subtitle = null,
  showNav = false
}) => {
  const { isSignedIn, isLoaded } = useUser();
  const { makeRequest } = useApi();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();

  // BYOK modal state
  const [showBYOKModal, setShowBYOKModal] = useState(false);
  const [userData, setUserData] = useState(null);

  // Fetch user data to check Pro tier eligibility
  useEffect(() => {
    // Wait for Clerk to fully load AND confirm user is signed in
    if (isLoaded && isSignedIn) {
      const fetchUserData = async () => {
        try {
          const data = await makeRequest('users/me');
          setUserData(data);
        } catch (err) {
          console.error('Failed to fetch user data:', err);
        }
      };
      fetchUserData();
    }
  }, [isLoaded, isSignedIn, makeRequest]);

  const handleManageSubscription = async () => {
    try {
      const { url } = await makeRequest('billing/customer-portal', {
        method: 'POST',
        body: JSON.stringify({
          return_url: window.location.href
        })
      });

      // Backend returns either:
      // 1. Customer portal URL (if active subscription)
      // 2. Recovery URL (if abandoned checkout exists)
      // The recovery URL automatically creates a new checkout session
      window.location.href = url;
    } catch (err) {
      // On any error, redirect to pricing section on landing page
      // This handles:
      // - No active subscription
      // - No recoverable checkout session
      // - Stripe API errors
      console.error('Subscription management error:', err);
      navigate('/');
      // Scroll to pricing section after navigation completes
      setTimeout(() => {
        document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };
  const getNavbarStyles = () => {
    const baseStyles = {
      ...sharedStyles.headerStyles,
      position: variant === 'report' ? 'sticky' : 'relative',
      top: variant === 'report' ? 0 : 'auto',
      zIndex: 100,
    };

    switch (variant) {
      case 'landing':
        return {
          ...baseStyles,
          position: 'sticky',
          top: 0,
          zIndex: 1000
        };
      case 'form':
      case 'report':
        return {
          ...baseStyles,
          boxShadow: '0px 8px 32px rgba(15, 23, 42, 0.06)'
        };
      default:
        return baseStyles;
    }
  };

  const getContainerStyles = () => {
    return {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 1rem'
    };
  };

  const getHeaderContentStyles = () => {
    return {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '1.5rem 0'
    };
  };

  const logoStyles = {
    display: 'flex',
    alignItems: 'center',
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: sharedStyles.colors.textPrimary,
    textDecoration: 'none'
  };

  const logoIconStyles = {
    width: '2rem',
    height: '2rem',
    color: sharedStyles.colors.primary,
    marginRight: '0.5rem'
  };

  const logoTextStyles = {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: sharedStyles.colors.textPrimary,
    margin: 0
  };

  const navStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '2rem'
  };

  const navLinkStyles = {
    color: sharedStyles.colors.textSecondary,
    textDecoration: 'none',
    fontWeight: '500',
    fontSize: '0.95rem',
    transition: 'color 0.2s ease',
    padding: '0.5rem 0'
  };

  const toggleTheme = () => {
    if (theme === 'light') setTheme('dark');
    else if (theme === 'dark') setTheme('system');
    else setTheme('light');
  };

  const getThemeIcon = () => {
    if (theme === 'light') return <Sun size={20} />;
    if (theme === 'dark') return <Moon size={20} />;
    return <Monitor size={20} />;
  };

  return (
    <header style={getNavbarStyles()}>
      <div style={getContainerStyles()}>
        <div style={getHeaderContentStyles()}>
          {/* Logo */}
          <Link to={isSignedIn ? "/dashboard" : "/"} style={logoStyles}>
            <Mic style={logoIconStyles} />
            <span style={logoTextStyles}>Conviviality</span>
            {subtitle && (
              <span style={{
                marginLeft: '1rem',
                fontSize: '0.875rem',
                color: sharedStyles.colors.textSecondary,
                fontWeight: 'normal'
              }}>
                {subtitle}
              </span>
            )}
          </Link>

          {/* Navigation */}
          <nav style={navStyles}>
            {/* Show marketing nav for landing page */}
            {showNav && !isSignedIn && (
              <>
                <a
                  href="#features"
                  style={navLinkStyles}
                  onMouseEnter={e => e.target.style.color = sharedStyles.colors.primary}
                  onMouseLeave={e => e.target.style.color = sharedStyles.colors.textSecondary}
                >
                  Features
                </a>
                <a
                  href="#how-it-works"
                  style={navLinkStyles}
                  onMouseEnter={e => e.target.style.color = sharedStyles.colors.primary}
                  onMouseLeave={e => e.target.style.color = sharedStyles.colors.textSecondary}
                >
                  How It Works
                </a>
                <a
                  href="#about"
                  style={navLinkStyles}
                  onMouseEnter={e => e.target.style.color = sharedStyles.colors.primary}
                  onMouseLeave={e => e.target.style.color = sharedStyles.colors.textSecondary}
                >
                  About
                </a>
                <a
                  href="#pricing"
                  style={navLinkStyles}
                  onMouseEnter={e => e.target.style.color = sharedStyles.colors.primary}
                  onMouseLeave={e => e.target.style.color = sharedStyles.colors.textSecondary}
                >
                  Pricing
                </a>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{
                    width: '1px',
                    height: '20px',
                    backgroundColor: sharedStyles.colors.primary
                  }} />
                  <SignInButton mode="modal">
                    <button
                      style={{
                        color: sharedStyles.colors.textSecondary,
                        textDecoration: 'none',
                        fontWeight: '500',
                        fontSize: '0.875rem',
                        transition: 'color 0.2s ease',
                        padding: '0.5rem 0',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={e => e.target.style.color = sharedStyles.colors.primary}
                      onMouseLeave={e => e.target.style.color = sharedStyles.colors.textSecondary}
                    >
                      Sign In
                    </button>
                  </SignInButton>
                </div>
              </>
            )}

            {/* Show dashboard nav for signed-in users */}
            {isLoaded && isSignedIn && (
              <>
                <Link
                  to="/dashboard"
                  style={navLinkStyles}
                  onMouseEnter={e => e.target.style.color = sharedStyles.colors.primary}
                  onMouseLeave={e => e.target.style.color = sharedStyles.colors.textSecondary}
                >
                  Dashboard
                </Link>
                {/* Clerk's built-in user button with profile menu */}
                <UserButton
                  appearance={{
                    elements: {
                      avatarBox: "w-10 h-10"
                    }
                  }}
                >
                  <UserButton.MenuItems>
                    {/* Show API Settings for Pro tier users only */}
                    {(userData?.subscription_tier === 'pro' || userData?.is_lifetime) && (
                      <UserButton.Action
                        label="API Settings"
                        labelIcon={<Key size={16} />}
                        onClick={() => setShowBYOKModal(true)}
                      />
                    )}
                    <UserButton.Action label="Manage subscription" labelIcon={<CreditCard size={16} />} onClick={handleManageSubscription} />
                  </UserButton.MenuItems>
                </UserButton>
              </>
            )}
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '0.5rem',
                color: sharedStyles.colors.textSecondary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                transition: 'all 0.2s ease'
              }}
              title={`Current theme: ${theme}`}
              onMouseEnter={e => e.target.style.background = 'rgba(0,0,0,0.05)'}
              onMouseLeave={e => e.target.style.background = 'transparent'}
            >
              {getThemeIcon()}
            </button>
          </nav>
        </div>
      </div>

      {/* BYOK Settings Modal */}
      <BYOKSettingsModal
        isOpen={showBYOKModal}
        onClose={() => setShowBYOKModal(false)}
      />
    </header>
  );
};

export default Navbar;